import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
} from "mdb-react-ui-kit";
import { getTrash, restoreFile, deletePermanently, mfmThumbUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import "../MFMHomeView/MFMHomeView.css";

const TRASH_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const d = new Date(String(value).replace(/\[[^\]]+\]$/, ""));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTrashDate(value) {
  const d = parseDate(value);
  if (!d) return "-";
  return d.toLocaleDateString();
}

function formatFileSize(bytes) {
  if (bytes == null || bytes === 0) return "0 KB";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) { size /= 1024; i += 1; }
  return (i === 0 ? size : size.toFixed(1).replace(/\.0$/, "")) + " " + units[i];
}

function getTrashedAt(file) {
  return file?.trashedAt ?? file?.trashed_at ?? file?.deletedAt ?? file?.deleted_at ?? null;
}

function formatCountdown(trashedAt, nowMs) {
  const d = parseDate(trashedAt);
  if (!d) return "-";

  const remainingMs = d.getTime() + TRASH_TTL_MS - nowMs;
  if (remainingMs <= 0) return "Expirado (pendiente de limpieza)";

  const remainingSec = Math.ceil(remainingMs / 1000);
  if (remainingSec < 60) return "Se borrara en " + remainingSec + " s";
  const remainingMin = Math.ceil(remainingSec / 60);
  if (remainingMin < 60) return "Se borrara en " + remainingMin + " min";
  const remainingHours = Math.ceil(remainingMin / 60);
  if (remainingHours < 24) return "Se borrara en " + remainingHours + " h";
  const remainingDays = Math.ceil(remainingHours / 24);
  return "Se borrara en " + remainingDays + (remainingDays === 1 ? " dia" : " dias");
}

export default function MFMTrashView() {
  const [trashFiles, setTrashFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());

  const loadTrash = useCallback(async () => {
    setLoading(true);
    try {
      const files = await getTrash();
      setTrashFiles(Array.isArray(files) ? files : []);
      setNowMs(Date.now());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const sortedTrash = useMemo(
    () => [...trashFiles].sort((a, b) => new Date(getTrashedAt(b) || 0) - new Date(getTrashedAt(a) || 0)),
    [trashFiles]
  );

  return (
    <div className="mfm-home p-4">
      <MDBRow className="mb-3 align-items-center">
        <MDBCol>
          <h5><MDBIcon fas icon="trash-alt" className="me-2" />Papelera</h5>
          <small className="text-muted">Los ficheros se eliminan permanentemente a los 30 días.</small>
        </MDBCol>
        <MDBCol className="text-end">
          <MDBBtn size="sm" color="light" onClick={loadTrash}><MDBIcon fas icon="sync" /></MDBBtn>
        </MDBCol>
      </MDBRow>

      {loading ? (
        <div className="text-center py-4"><MDBSpinner /></div>
      ) : sortedTrash.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <MDBIcon fas icon="check-circle" size="2x" className="mb-2 text-success" />
          <p>La papelera está vacía</p>
        </div>
      ) : (
        sortedTrash.map((file) => {
          const trashedAt = getTrashedAt(file);
          return (
            <MDBCard key={file.uuid} className="mb-2">
              <MDBCardBody className="d-flex align-items-center gap-3 py-2">
                <div style={{ width: 48, height: 48, flexShrink: 0, background: "#eee", borderRadius: 4, overflow: "hidden" }}>
                  {file.mediaType === "image"
                    ? <img src={mfmThumbUrl(file.uuid)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div className="d-flex align-items-center justify-content-center h-100"><MDBIcon fas icon="play-circle" /></div>}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="text-truncate fw-semibold">{file.title || file.originalName}</div>
                  <small className="text-muted">
                    Eliminado: {formatTrashDate(trashedAt)}
                    &nbsp;·&nbsp;
                    {formatFileSize(file.fileSize)}
                  </small>
                  <small className="text-muted d-block">{formatCountdown(trashedAt, nowMs)}</small>
                  <small className="text-muted d-block">{file.appCode || "-"}/{file.libraryName || "-"}</small>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                  <MDBBtn size="sm" color="success" outline onClick={async () => { await restoreFile(file.uuid); loadTrash(); }}>
                    <MDBIcon fas icon="undo" className="me-1" />Restaurar
                  </MDBBtn>
                  <MDBBtn
                    size="sm"
                    color="danger"
                    outline
                    onClick={async () => {
                      if (!window.confirm("¿Eliminar permanentemente? Esta acción no se puede deshacer.")) return;
                      await deletePermanently(file.uuid);
                      loadTrash();
                    }}
                  >
                    <MDBIcon fas icon="times" className="me-1" />Eliminar
                  </MDBBtn>
                </div>
              </MDBCardBody>
            </MDBCard>
          );
        })
      )}
    </div>
  );
}
